/**
 * Milestone Table Copy-to-Clipboard Utility
 *
 * Generates HTML that matches the exact UI styling including:
 * - Adobe Clean font
 * - Exact cell colors and borders with vertical separators
 * - Icons as base64 encoded SVGs matching UI exactly
 * - Proper TBD vs N/A handling
 * - Links with proper styling
 * - Striped patterns for "Not Required" statuses
 */

import { ToastQueue } from '@adobe/react-spectrum';
import {
  formatEnhancedCellDisplay,
  getEnhancedCellStyling,
  getEnhancedMilestoneIcon,
  getDateForPastCheck,
  ENHANCED_MILESTONE_STATUSES
} from './milestoneConfigEnhanced';
import { isMilestoneDateInPast } from '../../../pages/MilestonesGrid/utils/milestoneSortUtils';
import { milestoneAttributesService } from '../../../services/milestoneAttributesService';
import type { MilestoneAttributesData } from '../../../services/milestoneAttributesService';

export interface MilestoneForCopy {
  name: string;
  status?: string;
  typeOfReview?: string;
  reviewType?: string;
  approvalCondition?: string;
  approver?: string;
  dateTime?: string;
  date?: string;
  meetingScheduledAt?: string; // ✅ FIX: Time field for SCHEDULED status
  secondaryDate?: string;
  documentLink?: string;
  recordingLink?: string;
  milestoneAttributes?: {
    approvalRequired?: boolean;
    async?: boolean;
  };
}

/**
 * Match MilestoneMatrixFromAPI.effectiveReviewType — prefer API `reviewType` over legacy `typeOfReview`.
 */
export const effectiveReviewTypeForCopy = (
  milestone: MilestoneForCopy | Record<string, unknown>
): string | undefined => {
  const m = milestone as MilestoneForCopy & { type_of_review?: string };
  return m.reviewType || m.typeOfReview || m.type_of_review;
};

/**
 * Match MilestoneMatrixFromAPI value-cell TBD rendering (reviewType TBD wins over computed status).
 */
export const shouldShowMilestoneTbd = (
  milestone: MilestoneForCopy,
  reviewType?: string
): boolean => {
  const rt = reviewType ?? effectiveReviewTypeForCopy(milestone);
  return (
    rt === 'TBD' ||
    rt === 'tbd' ||
    (milestone.status === 'TBD' &&
      rt !== 'Milestone Not Required' &&
      rt !== 'Not Required')
  );
};

/** Outlook/Word paste works more reliably with StartFragment markers. */
const wrapHtmlForClipboard = (html: string): string =>
  `<!DOCTYPE html><html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word"><head><meta charset="utf-8"></head><body><!--StartFragment-->${html}<!--EndFragment--></body></html>`;

/**
 * Get milestone attributes from API response (same logic as MilestoneMatrixFromAPI)
 */
const getMilestoneAttributesFromData = (
  milestoneName: string,
  attributesData: MilestoneAttributesData | null
): { async: boolean; approvalRequired: boolean } | undefined => {
  if (!attributesData?.milestone_attributes) {
    return undefined;
  }

  // Try exact match first
  let attributes = attributesData.milestone_attributes[milestoneName];

  // If no exact match, try some common variations
  if (!attributes) {
    // Try without spaces
    const noSpaceName = milestoneName.replace(/\s+/g, '');
    attributes = attributesData.milestone_attributes[noSpaceName];
  }

  // Try with spaces removed and check common mappings
  if (!attributes) {
    const mappings: { [key: string]: string } = {
      'Creative Arch': 'CreativeArch',
      'Media Brainstorm': 'MediaPosition',
      'Mktg Pkg': 'MktgPkg',
      'CA/APS': 'CA/APS',
      MVP: 'MVP',
      GTMS: 'GTMS',
      BRND: 'BRND',
      MONTZN: 'MONTZN',
      GTMP: 'GTMP',
      E2E1: 'E2E1',
      E2E2: 'E2E2',
      RR1: 'RR1',
      RR2: 'RR2',
      ReleaseMSG: 'ReleaseMSG'
    };

    const mappedName = mappings[milestoneName];
    if (mappedName) {
      attributes = attributesData.milestone_attributes[mappedName];
    }
  }

  if (!attributes) {
    return undefined;
  }

  return {
    async: attributes.async || false,
    approvalRequired: attributes.approvalRequired || false
  };
};

/**
 * Enhance milestones with attributes from API
 */
const enhanceMilestonesWithAttributes = async (
  milestones: any[]
): Promise<any[]> => {
  try {
    const attributesData =
      await milestoneAttributesService.getMilestoneAttributes();

    console.log('🔍 Milestone attributes data loaded:', attributesData);

    return milestones.map(milestone => ({
      ...milestone,
      milestoneAttributes: getMilestoneAttributesFromData(
        milestone.name,
        attributesData
      )
    }));
  } catch (error) {
    console.error('Failed to load milestone attributes:', error);
    // Return milestones without enhancement if service fails
    return milestones;
  }
};

/**
 * Generates HTML table string from milestones data matching exact UI styling
 */
const generateMilestoneTableHTML = (
  milestones: MilestoneForCopy[],
  momentName?: string,
  /** Name of the milestone to highlight (the one being edited). */
  highlightMilestoneName?: string
): string => {
  const normalizedHighlightName = highlightMilestoneName?.trim().toUpperCase();

  // Table header row
  const headerCells = milestones
    .map((milestone, index) => {
      const isLastCell = index === milestones.length - 1;
      const isHighlighted =
        !!normalizedHighlightName &&
        milestone.name?.trim().toUpperCase() === normalizedHighlightName;

      // Extract reviewType for async detection
      const reviewType = effectiveReviewTypeForCopy(milestone);

      // Approval "A" - red text, no background (matches UI exactly)
      // Outlook-compatible: Use inline-block with margins instead of flexbox
      const approvalIcon = milestone.milestoneAttributes?.approvalRequired
        ? `<span style="
            color: #D31510;
            font-weight: 800;
            font-size: 14px;
            font-family: 'Adobe Clean', 'Helvetica Neue', Arial, sans-serif;
            line-height: 1.3;
            margin-left: 6px;
            display: inline-block;
            vertical-align: middle;
          ">A</span>`
        : '';

      // Async email icon - Check multiple sources to determine if milestone is async
      // 1. Check milestoneAttributes.async (from API)
      // 2. Check milestone.async (direct field)
      // 3. Check reviewType === 'Async' (fallback)
      const isAsync =
        milestone.milestoneAttributes?.async === true ||
        (milestone as any).async === true ||
        reviewType === 'Async' ||
        reviewType === 'async' ||
        false;

      // DEBUG: Log async detection for each milestone in header
      console.log('🔍 Header async detection:', {
        name: milestone.name,
        reviewType,
        'milestoneAttributes?.async': milestone.milestoneAttributes?.async,
        '(milestone as any).async': (milestone as any).async,
        isAsync
      });

      const asyncIcon = isAsync
        ? `<a href="https://wiki.corp.adobe.com/x/GKLAvg" 
            target="_blank" 
            rel="noopener noreferrer"
            style="text-decoration: none; display: inline-block; vertical-align: middle; margin-left: 6px;">
            <span style="
              color: #147AF3;
              font-size: 12px;
              line-height: 1;
              vertical-align: middle;
            ">✉</span>
          </a>`
        : '';

      // Wrap only long multi-word names (e.g. "MEDIA BRAINSTORM", "CREATIVE ARCH")
      // onto two lines via an explicit <br/> — email clients (Outlook) don't
      // reliably honor white-space/word-wrap, but an explicit <br/> still breaks
      // under nowrap. Short names like "MKTG PKG" stay on one line. Threshold
      // matches the ">12 chars" long-name check in milestoneDisplayUtils.getCellWidthClass.
      const nameHtml =
        milestone.name?.includes(' ') && milestone.name.length > 12
          ? milestone.name.replace(' ', '<br/>')
          : milestone.name;

      return `
        <th style="
          display: table-cell;
          vertical-align: middle;
          font-family: 'Adobe Clean', 'Helvetica Neue', Arial, sans-serif;
          font-weight: 500;
          font-size: 14px;
          line-height: 1.3;
          color: #6D6D6D;
          padding: 8px 16px;
          background: #FFFFFF;
          ${
            isHighlighted
              ? 'border: 2px solid #F68511; border-bottom: none;'
              : `border-bottom: 1px solid #E6E6E6;${
                  isLastCell ? '' : 'border-right: 1px solid #E6E6E6;'
                }`
          }
          text-align: left;
          white-space: nowrap;
          text-transform: uppercase;
          box-sizing: border-box;
        ">
          <span>${nameHtml}</span>
          ${approvalIcon}
          ${asyncIcon}
        </th>
      `;
    })
    .join('');

  // Table data cells - match exact value row styling
  const dataCells = milestones
    .map((milestone, index) => {
      const isLastCell = index === milestones.length - 1;
      const isHighlighted =
        !!normalizedHighlightName &&
        milestone.name?.trim().toUpperCase() === normalizedHighlightName;
      const reviewType = effectiveReviewTypeForCopy(milestone);

      // Get status key from milestone status - safely convert to string first
      // Only use toUpperCase for config lookup, not for display
      let statusKey = '';
      if (milestone.status) {
        const statusStr =
          typeof milestone.status === 'string'
            ? milestone.status
            : String(milestone.status);
        // Skip if status looks like a number (e.g., HTTP status codes like 200)
        if (!/^\d+$/.test(statusStr.trim())) {
          statusKey = statusStr.toUpperCase();
        }
      }

      // Determine effective status key - match display logic
      // Special handling: If reviewType is "Not Required" or "Milestone Not Required", treat it as NOT_REQUIRED
      let effectiveStatusKey = statusKey;
      if (
        reviewType === 'Not Required' ||
        reviewType === 'Milestone Not Required'
      ) {
        effectiveStatusKey = 'NOT_REQUIRED';
      } else if (!statusKey) {
        // When status is null, check reviewType to determine status
        // If reviewType is "TBD", use TBD status configuration
        if (reviewType === 'TBD' || reviewType === 'tbd') {
          effectiveStatusKey = 'TBD';
        } else {
          // For other review types when status is null, default to TBD (matching display logic)
          effectiveStatusKey = 'TBD';
        }
      }

      const showTBD = shouldShowMilestoneTbd(milestone, reviewType);
      if (showTBD) {
        effectiveStatusKey = 'TBD';
      }

      // Get styling from the same functions used in UI
      const cellStyling = getEnhancedCellStyling(effectiveStatusKey, milestone);
      const displayText = formatEnhancedCellDisplay(
        effectiveStatusKey,
        milestone
      );
      const iconName = getEnhancedMilestoneIcon(effectiveStatusKey, {
        approvalCondition: milestone.milestoneAttributes?.approvalRequired
          ? 'Approval Required'
          : 'NO Approval Required',
        reviewType: reviewType || 'Meeting'
      });
      const statusConfig = ENHANCED_MILESTONE_STATUSES[effectiveStatusKey];

      // Format background - use cell color or default white
      let bgStyle = '';

      // ✅ Removed all striped patterns - use plain backgrounds for all statuses
      if (effectiveStatusKey === 'NOT_REQUIRED') {
        bgStyle = `background-color: #F3FFEE;`;
      } else {
        bgStyle = `background-color: ${
          cellStyling.backgroundColor || '#FFFFFF'
        };`;
      }

      // Format text color - use gray for NOT_REQUIRED cells
      // Apply red for past dates (check displayed date: secondary when shown, else primary)
      const isEndState = [
        'APPROVED',
        'APPROVED_ASYNC',
        'RE_APPROVED',
        'RE_APPROVED_ASYNC',
        'COMPLETE',
        'COMPLETE_ASYNC',
        'APPROVED_FOLLOW_UP'
      ].includes(effectiveStatusKey);
      const dateToCheck = getDateForPastCheck(milestone, effectiveStatusKey);
      const isPastDate =
        !isEndState && dateToCheck && isMilestoneDateInPast(dateToCheck);
      let textColor =
        effectiveStatusKey === 'NOT_REQUIRED'
          ? '#666666'
          : cellStyling.color || '#222222';
      if (isPastDate) {
        textColor = '#D31510';
      }

      // Format status icon - Use styled Unicode (email clients block inline SVG)
      // ✅ Do not render icons for NOT_REQUIRED status
      let iconHTML = '';
      if (iconName && effectiveStatusKey !== 'NOT_REQUIRED') {
        // Handle icon name mapping
        const iconToRender =
          iconName === 'envelope'
            ? 'email'
            : iconName === 'arrow-circle' || iconName === 'arrow_circle'
            ? 'refresh'
            : iconName === 'complete'
            ? 'checkmark'
            : iconName === 'send'
            ? 'email'
            : iconName;

        // Map icon names to styled Unicode symbols with exact UI colors
        const iconMap: Record<string, { symbol: string; color: string }> = {
          checkmark: { symbol: '✓', color: '#15A06C' },
          calendar: { symbol: '📅', color: '#EF5A98' },
          email: { symbol: '✉', color: '#147AF3' },
          refresh: { symbol: '↻', color: '#E46F00' },
          clock: { symbol: '🕐', color: '#6D6D6D' },
          warning: { symbol: '⚠', color: '#EA3829' }
        };

        const iconData = iconMap[iconToRender];
        if (iconData) {
          iconHTML = `<span style="
            color: ${iconData.color};
            font-size: 14px;
            line-height: 1;
            margin-right: 8px;
            vertical-align: middle;
            display: inline-block;
          ">${iconData.symbol}</span>`;
        }
      }

      // Add recording link icon - Styled Unicode video icon
      if (milestone.recordingLink) {
        iconHTML += `<a href="${milestone.recordingLink}" 
          style="
            text-decoration: none;
            margin-right: 8px;
            display: inline-block;
            color: #6D6D6D;
            vertical-align: middle;
          " 
          target="_blank" 
          rel="noopener noreferrer"><span style="
            font-size: 14px;
            vertical-align: middle;
          ">🎥</span></a>`;
      }

      // Determine what to show: TBD, Not Required, N/A, or display text
      let textHTML = '';
      let isEmptyOrNA = false;

      if (showTBD) {
        textHTML = `<span style="color: #EA3829; font-weight: 500;">TBD</span>`;
      } else if (effectiveStatusKey === 'NOT_REQUIRED') {
        // ✅ Show "N/A" for NOT_REQUIRED status (user request: show N/A in copied content)
        textHTML = `<span style="color: #666666; font-weight: 400;">N/A</span>`;
        isEmptyOrNA = true;
      } else if (
        effectiveStatusKey === '' ||
        !displayText ||
        displayText.trim() === '' ||
        displayText.toUpperCase() === 'N/A'
      ) {
        // Show N/A when status is null/empty (not TBD, not Not Required)
        textHTML = `<span style="color: #6D6D6D; font-weight: 400;">N/A</span>`;
        isEmptyOrNA = true;
      } else {
        // Show display text, with link if available
        // Use textColor for links when past date (red); otherwise blue to indicate link
        const linkColor = isPastDate ? textColor : '#147AF3';
        if (milestone.documentLink && !isEmptyOrNA) {
          textHTML = `<a href="${milestone.documentLink}" 
            style="
              color: ${linkColor};
              text-decoration: none;
              font-family: 'Adobe Clean', 'Helvetica Neue', Arial, sans-serif;
            " 
            target="_blank" 
            rel="noopener noreferrer">${displayText}</a>`;
        } else {
          textHTML = `<span style="color: ${textColor};">${displayText}</span>`;
        }
      }

      // Outlook-compatible: Use inline-block instead of flexbox, margins instead of gap
      // Ensure borders are always visible with !important for Outlook
      // ✅ All cells use simple structure now (no striped patterns)
      return `
        <td style="
          display: table-cell;
          vertical-align: middle;
          text-align: center;
          font-family: 'Adobe Clean', 'Helvetica Neue', Arial, sans-serif;
          font-weight: 400;
          font-size: 13px;
          line-height: 1.4;
          color: ${textColor};
          padding: 12px 16px;
          ${bgStyle}
          ${
            isHighlighted
              ? 'border: 2px solid #F68511 !important; border-top: none !important;'
              : `border-bottom: 1px solid #E6E6E6 !important;${
                  isLastCell
                    ? ''
                    : 'border-right: 1px solid #E6E6E6 !important;'
                }`
          }
          white-space: nowrap;
          box-sizing: border-box;
        ">
          ${iconHTML}
          ${textHTML}
        </td>
      `;
    })
    .join('');

  // Complete table HTML with exact UI styling
  const tableHTML = `
    <div style="
      font-family: 'Adobe Clean', 'Helvetica Neue', Arial, sans-serif;
      margin: 0;
      padding: 0;
    ">
      ${
        momentName
          ? `
        <h3 style="
          margin: 0 0 16px 0;
          font-family: 'Adobe Clean', 'Helvetica Neue', Arial, sans-serif;
          font-size: 16px;
          font-weight: 600;
          color: #222222;
          line-height: 1.4;
        ">${momentName} - Milestones</h3>
      `
          : ''
      }
      <table style="
        border-collapse: collapse;
        border: 1px solid #E6E6E6;
        border-radius: 4px;
        background: #FFFFFF;
        font-family: 'Adobe Clean', 'Helvetica Neue', Arial, sans-serif;
        width: auto;
        table-layout: auto;
        border-spacing: 0;
      " cellspacing="0" cellpadding="0" border="0">
        <thead>
          <tr style="height: 60px;">
            ${headerCells}
          </tr>
        </thead>
        <tbody>
          <tr style="height: 60px;">
            ${dataCells}
          </tr>
        </tbody>
      </table>
      <p style="
        margin: 12px 0 0 0;
        font-family: 'Adobe Clean', 'Helvetica Neue', Arial, sans-serif;
        font-size: 11px;
        color: #6D6D6D;
        line-height: 1.4;
      ">
        Generated from Adobe Roadmap on ${new Date().toLocaleDateString()}
      </p>
    </div>
  `;

  return tableHTML;
};

/**
 * Generates plain text version of the milestone table for fallback
 * Uses Unicode symbols for better visual representation
 */
const generateMilestoneTablePlainText = (
  milestones: MilestoneForCopy[],
  momentName?: string
): string => {
  let plainText = momentName
    ? `${momentName} - Milestones\n\n`
    : 'Milestones\n\n';

  // Header row with Unicode symbols
  const headers = milestones
    .map(m => {
      // Extract reviewType for async detection
      const reviewType = effectiveReviewTypeForCopy(m);

      let name = m.name;
      if (m.milestoneAttributes?.approvalRequired) name += ' A'; // Plain A for approval

      // Check multiple sources to determine if milestone is async
      const isAsync =
        m.milestoneAttributes?.async === true ||
        (m as any).async === true ||
        reviewType === 'Async' ||
        reviewType === 'async' ||
        false;

      // DEBUG: Log async detection for plain text header
      console.log('🔍 Plain text header async detection:', {
        name: m.name,
        reviewType,
        'milestoneAttributes?.async': m.milestoneAttributes?.async,
        isAsync
      });

      if (isAsync) name += ' ✉'; // Envelope for async
      return name.padEnd(25);
    })
    .join(' | ');

  plainText += headers + '\n';
  plainText += '-'.repeat(headers.length) + '\n';

  // Data row with status icons
  const dataRow = milestones
    .map(milestone => {
      const reviewType = effectiveReviewTypeForCopy(milestone);

      // Get status key from milestone status - safely convert to string first
      // Only use toUpperCase for config lookup, not for display
      let statusKey = '';
      if (milestone.status) {
        const statusStr =
          typeof milestone.status === 'string'
            ? milestone.status
            : String(milestone.status);
        // Skip if status looks like a number (e.g., HTTP status codes like 200)
        if (!/^\d+$/.test(statusStr.trim())) {
          statusKey = statusStr.toUpperCase();
        }
      }

      // Determine effective status key - match display logic
      // Special handling: If reviewType is "Not Required" or "Milestone Not Required", treat it as NOT_REQUIRED
      let effectiveStatusKey = statusKey;
      if (
        reviewType === 'Not Required' ||
        reviewType === 'Milestone Not Required'
      ) {
        effectiveStatusKey = 'NOT_REQUIRED';
      } else if (!statusKey) {
        // When status is null, check reviewType to determine status
        // If reviewType is "TBD", use TBD status configuration
        if (reviewType === 'TBD' || reviewType === 'tbd') {
          effectiveStatusKey = 'TBD';
        } else {
          // For other review types when status is null, default to TBD (matching display logic)
          effectiveStatusKey = 'TBD';
        }
      }

      const showTBD = shouldShowMilestoneTbd(milestone, reviewType);
      if (showTBD) {
        effectiveStatusKey = 'TBD';
      }

      const displayText = formatEnhancedCellDisplay(
        effectiveStatusKey,
        milestone
      );
      const iconName = getEnhancedMilestoneIcon(effectiveStatusKey, {
        approvalCondition: milestone.milestoneAttributes?.approvalRequired
          ? 'Approval Required'
          : 'NO Approval Required',
        reviewType: reviewType || 'Meeting'
      });

      // Handle TBD vs N/A — align with MilestoneMatrixFromAPI value cells
      let cellText = '';

      // Add status icon with Unicode symbols
      // ✅ Do not render icons for NOT_REQUIRED status
      if (iconName && effectiveStatusKey !== 'NOT_REQUIRED') {
        const iconToRender =
          iconName === 'envelope'
            ? 'email'
            : iconName === 'arrow-circle' || iconName === 'arrow_circle'
            ? 'refresh'
            : iconName === 'complete'
            ? 'checkmark'
            : iconName === 'send'
            ? 'email'
            : iconName;

        const unicodeMap: Record<string, string> = {
          checkmark: '✓', // Checkmark
          calendar: '📅', // Calendar
          email: '✉', // Envelope
          refresh: '↻', // Refresh/Replanning
          clock: '🕐', // Clock
          warning: '⚠' // Warning
        };

        const unicode = unicodeMap[iconToRender];
        if (unicode) {
          cellText = unicode + ' ';
        }
      }

      // Add display text
      if (showTBD) {
        cellText += 'TBD';
      } else if (effectiveStatusKey === 'NOT_REQUIRED') {
        // ✅ Show "Not Required" for NOT_REQUIRED status (not N/A)
        cellText += 'Not Required';
      } else if (
        !displayText ||
        displayText.trim() === '' ||
        effectiveStatusKey === ''
      ) {
        // Show N/A for empty status or when there's no display text
        cellText += 'N/A';
      } else {
        cellText += displayText;
      }

      if (milestone.recordingLink) cellText += ' 🎥'; // Video icon

      return cellText.padEnd(25);
    })
    .join(' | ');

  plainText += dataRow + '\n\n';
  plainText += `Generated from Adobe Roadmap on ${new Date().toLocaleDateString()}\n`;

  return plainText;
};

/**
 * Normalize milestone data to ensure all fields are accessible
 * Handles different data structures and field name variations
 */
const normalizeMilestoneForCopy = (milestone: any): MilestoneForCopy => {
  // Prefer reviewType (API / PATCH) over legacy typeOfReview — matches matrix UI
  const reviewType = effectiveReviewTypeForCopy(milestone);

  // Merge milestoneAttributes with fallback values
  // This ensures we get async and approvalRequired even if milestoneAttributes exists but doesn't have them
  // Also check reviewType === 'Async' to determine async flag
  // Priority: milestoneAttributes.async > milestone.async > reviewType === 'Async'
  const isAsyncFromReviewType =
    reviewType === 'Async' || reviewType === 'async';

  // Determine async flag with proper fallback chain
  let asyncFlag = false;
  if (milestone.milestoneAttributes?.async !== undefined) {
    asyncFlag = milestone.milestoneAttributes.async;
  } else if (milestone.async !== undefined) {
    asyncFlag = milestone.async;
  } else if (isAsyncFromReviewType) {
    asyncFlag = true;
  }

  // DEBUG: Log milestone normalization
  console.log('🔍 Normalizing milestone:', {
    name: milestone.name,
    reviewType,
    'milestone.milestoneAttributes?.async':
      milestone.milestoneAttributes?.async,
    'milestone.async': milestone.async,
    isAsyncFromReviewType,
    finalAsyncFlag: asyncFlag
  });

  const milestoneAttributes = {
    approvalRequired:
      milestone.milestoneAttributes?.approvalRequired ??
      milestone.approvalRequired ??
      milestone.required === true,
    async: asyncFlag
  };

  // ✅ FIX: Construct approvalCondition to match UI logic
  const approvalCondition = milestoneAttributes.approvalRequired
    ? 'Approval Required'
    : 'NO Approval Required';

  return {
    name: milestone.name || '',
    status: milestone.status,
    typeOfReview: reviewType,
    approvalCondition: approvalCondition, // ✅ FIX: Use computed value instead of milestone field
    approver: milestone.approver,
    dateTime: milestone.dateTime,
    date: milestone.date || milestone.meetingScheduledOn,
    meetingScheduledAt: milestone.meetingScheduledAt, // ✅ FIX: Include time for SCHEDULED status
    secondaryDate:
      milestone.secondaryDate ??
      milestone.dueDate ??
      milestone.pendingUntil ??
      milestone.followUpDate ??
      milestone.approvalDueDate ??
      milestone.reApprovedOn,
    documentLink: milestone.documentLink,
    recordingLink: milestone.recordingLink,
    milestoneAttributes
  };
};

/**
 * Flatten milestone containers to extract actual milestone objects
 * Handles both flat arrays of milestones and nested milestone containers
 */
const flattenMilestones = (milestones: any[]): MilestoneForCopy[] => {
  const flattened: MilestoneForCopy[] = [];

  for (const item of milestones) {
    // If this is a milestone container (has nested milestones array)
    if (item.milestones && Array.isArray(item.milestones)) {
      // Extract and normalize the nested milestones
      const nestedMilestones = item.milestones.map(normalizeMilestoneForCopy);
      flattened.push(...nestedMilestones);
    } else {
      // This is already a milestone object, normalize it
      flattened.push(normalizeMilestoneForCopy(item));
    }
  }

  return flattened;
};

/**
 * Generates the milestone table as a raw HTML string, ready to be sent to the
 * backend as an email template (e.g. the `emailTemplate` field in the
 * Send-for-Approval PATCH payload).
 *
 * Reuses the exact same enhance → flatten → HTML generation pipeline as
 * {@link copyMilestoneTableToClipboard}, so the emailed table always matches
 * what the "Copy milestones" button produces. Returns an empty string when
 * there are no milestones to render.
 */
export const generateMilestoneEmailTemplate = async (
  milestones: MilestoneForCopy[] | any[],
  momentName?: string,
  /** Name of the milestone being edited — highlighted in the emailed table. */
  highlightMilestoneName?: string
): Promise<string> => {
  if (!milestones || milestones.length === 0) {
    return '';
  }

  // Enhance with attributes from API (async/approvalRequired), then flatten &
  // normalize — identical to the copy-to-clipboard flow.
  const enhancedMilestones = await enhanceMilestonesWithAttributes(milestones);
  const normalizedMilestones = flattenMilestones(enhancedMilestones);

  if (normalizedMilestones.length === 0) {
    return '';
  }

  const res = generateMilestoneTableHTML(
    normalizedMilestones,
    momentName,
    highlightMilestoneName
  );
  return res;
};

/**
 * Main function to copy milestone table to clipboard
 * Supports both HTML (for rich email clients) and plain text fallback
 */
export const copyMilestoneTableToClipboard = async (
  milestones: MilestoneForCopy[] | any[],
  momentName?: string
): Promise<void> => {
  try {
    if (!milestones || milestones.length === 0) {
      ToastQueue.negative('No milestones to copy', { timeout: 3000 });
      return;
    }

    // DEBUG: Log raw milestones received
    console.log('🔍 Raw milestones received:', milestones);

    // Enhance milestones with attributes from API before processing
    const enhancedMilestones = await enhanceMilestonesWithAttributes(
      milestones
    );

    console.log('🔍 Enhanced milestones with attributes:', enhancedMilestones);

    // Flatten and normalize milestone data to ensure consistent structure
    // Handles both flat arrays and nested milestone containers
    const normalizedMilestones = flattenMilestones(enhancedMilestones);

    // DEBUG: Log normalized milestones
    console.log('🔍 Normalized milestones:', normalizedMilestones);

    if (normalizedMilestones.length === 0) {
      ToastQueue.negative('No milestones to copy', { timeout: 3000 });
      return;
    }

    // Generate both HTML and plain text versions
    const htmlContent = generateMilestoneTableHTML(
      normalizedMilestones,
      momentName
    );
    const plainTextContent = generateMilestoneTablePlainText(
      normalizedMilestones,
      momentName
    );

    // DEBUG: Log generated content preview
    console.log(
      '🔍 Generated HTML preview (first 500 chars):',
      htmlContent.substring(0, 500)
    );
    console.log('🔍 Generated plain text:', plainTextContent);

    // Modern Clipboard API with HTML support
    if (navigator.clipboard && window.ClipboardItem) {
      const htmlBlob = new Blob([wrapHtmlForClipboard(htmlContent)], {
        type: 'text/html'
      });
      const textBlob = new Blob([plainTextContent], { type: 'text/plain' });

      const clipboardItem = new ClipboardItem({
        'text/html': htmlBlob,
        'text/plain': textBlob
      });

      await navigator.clipboard.write([clipboardItem]);

      ToastQueue.positive(
        'Milestones copied! You can now paste into your email.',
        {
          timeout: 5000
        }
      );
    } else {
      // Fallback for older browsers - copy plain text only
      const textArea = document.createElement('textarea');
      textArea.value = plainTextContent;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();

      try {
        document.execCommand('copy');
        ToastQueue.positive('Milestones copied as plain text!', {
          timeout: 5000
        });
      } catch (err) {
        ToastQueue.negative('Failed to copy milestones', { timeout: 3000 });
      } finally {
        document.body.removeChild(textArea);
      }
    }
  } catch (error) {
    console.error('Error copying milestones:', error);
    ToastQueue.negative('Failed to copy milestones. Please try again.', {
      timeout: 3000
    });
  }
};

/**
 * Check if the browser supports rich HTML clipboard
 */
export const supportsRichClipboard = (): boolean => {
  return !!(navigator.clipboard && window.ClipboardItem);
};

/**
 * Helper to copy just the milestone data as JSON (for debugging or API use)
 */
export const copyMilestoneDataAsJSON = async (
  milestones: MilestoneForCopy[]
): Promise<void> => {
  try {
    const jsonString = JSON.stringify(milestones, null, 2);
    await navigator.clipboard.writeText(jsonString);
    ToastQueue.positive('Milestone data copied as JSON', { timeout: 3000 });
  } catch (error) {
    console.error('Error copying JSON:', error);
    ToastQueue.negative('Failed to copy JSON data', { timeout: 3000 });
  }
};
